import type { Backlink } from '@affine/core/modules/doc-link';
import type { MarkdownAdapter } from '@blocksuite/affine/shared/adapters';
import { type Store, toDraftModel } from '@blocksuite/affine/store';

export type BacklinkGroup = {
  docId: string;
  title: string;
  links: Backlink[];
};

export function groupBacklinks(links: Backlink[]): BacklinkGroup[] {
  const groups = new Map<string, BacklinkGroup>();
  const seen = new Set<string>();
  for (const link of links) {
    const key = JSON.stringify([link.docId, link.blockId]);
    if (seen.has(key)) continue;
    seen.add(key);
    let group = groups.get(link.docId);
    if (!group) {
      group = { docId: link.docId, title: link.title, links: [] };
      groups.set(link.docId, group);
    }
    group.links.push(link);
  }
  return [...groups.values()];
}

export function getBacklinkSearchParams(link: Backlink) {
  const displayMode = link.displayMode || 'page';
  let blockId = link.blockId;
  if (link.parentFlavour === 'affine:database' && link.parentBlockId) {
    blockId = link.parentBlockId;
  } else if (displayMode === 'edgeless' && link.noteBlockId) {
    blockId = link.noteBlockId;
  }
  return new URLSearchParams({ mode: displayMode, blockIds: blockId });
}

export async function renderBacklinkBlock(
  store: Store,
  blockId: string,
  adapter: MarkdownAdapter
) {
  const block = store.getBlock(blockId);
  if (!block) return undefined;
  // The source block is the identity of a mention. Including its children
  // would repeat other mentions in the group, just like context previews do.
  const draft = toDraftModel(block.model);
  draft.children = [];
  return (await adapter.fromBlock(draft))?.file.trim() || undefined;
}
